import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { formatMoney, isVideoUrl } from "../lib/api";
import { Trash2, Minus, Plus } from "lucide-react";

const Cart = () => {
  const { cart, updateQty, removeFromCart, cartSubtotalIdr, cartSubtotalUsd, currency, t, lang, lineKey } = useApp();
  const navigate = useNavigate();

  if (!cart.length) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="font-script text-3xl text-holy-red mb-2">{t("your_cart")}</div>
        <p className="text-muted-foreground mb-6">{t("empty_cart")}</p>
        <Link to="/shop" className="inline-flex bg-holy-red text-ivory rounded-full px-8 py-3 hover:-translate-y-0.5 transition-all" data-testid="empty-cart-shop-btn">
          {t("continue_shopping")}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-serif text-4xl text-holy-red mb-8">{t("your_cart")}</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3">
          {cart.map((it) => {
            const key = `${it.product_id}-${it.bundle_qty || 0}`;
            const isBundle = (it.bundle_qty || 0) > 1;
            return (
            <div key={lineKey(it)} className="flex gap-4 bg-white rounded-2xl p-4 border border-border/60" data-testid={`cart-item-${key}`}>
              <div className="w-24 h-24 bg-ivory rounded-xl flex items-center justify-center overflow-hidden shrink-0">
                {it.image && (
                  isVideoUrl(it.image) ? (
                    <video
                      src={it.image}
                      muted
                      playsInline
                      preload="metadata"
                      onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img src={it.image} alt={it.name} className="w-full h-full object-contain" />
                  )
                )}
              </div>
              <div className="flex-1">
                <Link to={`/product/${it.slug}`} className="font-serif text-lg leading-tight hover:text-holy-red">{it.name}</Link>
                {isBundle ? (
                  <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`pack-price-${key}`}>
                    {lang === "id" ? "Paket isi" : "Pack of"} {it.bundle_qty} pcs · {formatMoney(it.price_idr, it.price_usd, currency)}/{lang === "id" ? "paket" : "pack"}
                  </div>
                ) : it.quantity > 1 && (
                  <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`unit-price-${key}`}>
                    {formatMoney(it.price_idr, it.price_usd, currency)} /pc
                  </div>
                )}
                <div className="text-holy-red text-sm font-semibold mt-1" data-testid={`line-total-${key}`}>
                  {formatMoney(it.price_idr * it.quantity, it.price_usd * it.quantity, currency)}
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex items-center border border-border rounded-full overflow-hidden text-xs">
                    <button onClick={() => updateQty(it, it.quantity - 1)} className="px-2 py-1 hover:bg-cream" data-testid={`qty-minus-${key}`}><Minus className="w-3 h-3" /></button>
                    <span className="px-3" data-testid={`qty-${key}`}>{it.quantity}{isBundle ? (lang === "id" ? " paket" : " pack") : ""}</span>
                    <button onClick={() => updateQty(it, it.quantity + 1)} className="px-2 py-1 hover:bg-cream" data-testid={`qty-plus-${key}`}><Plus className="w-3 h-3" /></button>
                  </div>
                  <button onClick={() => removeFromCart(it)} className="text-xs text-muted-foreground hover:text-holy-red flex items-center gap-1" data-testid={`remove-${key}`}>
                    <Trash2 className="w-3.5 h-3.5" /> {lang === "id" ? "Hapus" : "Remove"}
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>

        <aside className="bg-cream rounded-2xl p-6 h-fit">
          <h3 className="font-serif text-xl mb-4">Summary</h3>
          <div className="flex justify-between py-2 text-sm">
            <span>{t("subtotal")}</span>
            <span data-testid="cart-subtotal">{formatMoney(cartSubtotalIdr, cartSubtotalUsd, currency)}</span>
          </div>
          <div className="flex justify-between py-2 text-sm text-muted-foreground">
            <span>{t("shipping")}</span>
            <span>{lang === "id" ? "Hitung saat checkout" : "Calculated at checkout"}</span>
          </div>
          <button
            data-testid="checkout-btn"
            onClick={() => navigate("/checkout")}
            className="w-full bg-holy-red text-ivory rounded-full py-3.5 mt-4 hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            {t("checkout")} →
          </button>
          <Link to="/shop" className="block text-center mt-3 text-xs uppercase tracking-widest text-muted-foreground hover:text-holy-red">
            {t("continue_shopping")}
          </Link>
        </aside>
      </div>
    </div>
  );
};

export default Cart;
